

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewTripComponentModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        price: '',
        departure_city: '',
        net_cost: '',
        description: '',
        status: 'Active',
        sub_category: '',
        trip_id: '',
        trip_option_id: ''
    });

    const [trips, setTrips] = useState([]);
    const [tripOptions, setTripOptions] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    const fetchData = async () => {
        setLoadingData(true);
        try {
            const [tripsRes, optionsRes] = await Promise.all([
                axiosInstance.get('/trips/'),
                axiosInstance.get('/trip-options/')
            ]);
            setTrips(tripsRes.data);
            setTripOptions(optionsRes.data);
        } catch (error) {
            console.error('Error fetching modal data:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                trip_id: parseInt(formData.trip_id),
                trip_option_id: formData.trip_option_id ? parseInt(formData.trip_option_id) : null,
                price: formData.price ? parseFloat(formData.price) : null,
                net_cost: formData.net_cost ? parseFloat(formData.net_cost) : null,
            };

            await onSave(payload);
            onClose();
            // Reset form
            setFormData({
                name: '', category: '', price: '', departure_city: '',
                net_cost: '', description: '', status: 'Active',
                sub_category: '', trip_id: '', trip_option_id: ''
            });
        } catch (error) {
            console.error('Error saving Trip Logistics:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Trip Logistics',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const categoryOptions = ['Accommodation', 'Meal', 'Transportation', 'Activity', 'Misc'];
    const accomSubCategories = ['shared dorms', 'basic guesthouses + twinshare', 'twinshare hotels', 'expedition travellers'];

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '800px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Trip Logistics</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4 }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            label="Category"

                                        >
                                            {categoryOptions.map(cat => (
                                                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {formData.category === 'Accommodation' && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal">
                                            <InputLabel>Sub Category</InputLabel>
                                            <Select
                                                name="sub_category"
                                                value={formData.sub_category}
                                                onChange={handleInputChange}
                                                label="Sub Category"

                                            >
                                                {accomSubCategories.map(sub => (
                                                    <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Trip</InputLabel>
                                        <Select
                                            name="trip_id"
                                            value={formData.trip_id}
                                            onChange={handleInputChange}
                                            label="Trip"

                                        >
                                            {trips.map(t => (
                                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Trip Option</InputLabel>
                                        <Select
                                            name="trip_option_id"
                                            value={formData.trip_option_id}
                                            onChange={handleInputChange}
                                            label="Trip Option"

                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {tripOptions.map(opt => (
                                                <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="List Price"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        inputProps={{ step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Net Cost"
                                        name="net_cost"
                                        value={formData.net_cost}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        inputProps={{ step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Departure City"
                                        name="departure_city"
                                        value={formData.departure_city}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Component'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTripComponentModal;
